//@ts-nocheck
import { Action, Thunk, action, thunk } from "easy-peasy";
import { post, get } from "requests";

export interface DocumentTemplateModel {
  documentTemplates: any[];
  additionalTemplateOptions: any[];
  loading: boolean;
  error: string | null;
  setDocumentTemplates: Action<DocumentTemplateModel, any[]>;
  setLoading: Action<DocumentTemplateModel, boolean>;
  setError: Action<DocumentTemplateModel, string | null>;
  setAdditionalTemplateOptions: Action<DocumentTemplateModel, any[]>;
  fetchDocumentTemplates: Thunk<DocumentTemplateModel, { source: string; id: number }>;
  generateDocumentFromTemplate: Thunk<
    DocumentTemplateModel,
    { params: any; callBack: (res: any) => void }
  >;
  generateDocumentWithMultipleTemplates: Thunk<
    DocumentTemplateModel,
    { params: any; callBack: (res: any) => void }
  >;
  loadDocumentTemplate: Thunk<DocumentTemplateModel, { documentSource: string; id: number }>;
  getJasperSubTemplateList: Thunk<DocumentTemplateModel, { source: string }>;
  getFunderBankDetails: Thunk<DocumentTemplateModel, { dealId: string }>;
  fetchDocumentTemplatesByFunder: Thunk<
    DocumentTemplateModel,
    { documentSource: string; funder: string }
  >;
}

const documentTemplateModel: DocumentTemplateModel = {
  documentTemplates: [],
  additionalTemplateOptions: [],
  loading: false,
  error: null,

  setDocumentTemplates: action((state, templates) => {
    state.documentTemplates = templates;
  }),

  setLoading: action((state, loading) => {
    state.loading = loading;
  }),

  setError: action((state, error) => {
    state.error = error;
  }),

  setAdditionalTemplateOptions: action((state, options) => {
    state.additionalTemplateOptions = options;
  }),

  loadDocumentTemplate: thunk(async (actions, request, { getState }) => {
    const documentSource = request?.documentSource;
    const id = request?.id;

    actions.setLoading(true);
    actions.setError(null);

    const requestPath =
      documentSource === "Deal" ? "getCurrentFunderByDealId/" : "getCurrentFunderByApplicationId/";

    try {
      const res = await get(requestPath + id);

      const funderName = res["funderName"];

      const documentTemplateOptions = await actions.fetchDocumentTemplatesByFunder({
        documentSource,
        funderName,
      });

      actions.setDocumentTemplates(documentTemplateOptions);

      actions.getJasperSubTemplateList({ source: documentSource });
    } catch (error: any) {
      actions.setError("Error loading document templates: " + error.message);
    } finally {
      actions.setLoading(false);
    }
  }),

  fetchDocumentTemplates: thunk(async (actions, { source, id }) => {
    actions.setLoading(true);
    actions.setError(null);

    try {
      const response = await get("/documentTemplates");

      if (response && Array.isArray(response)) {
        actions.setDocumentTemplates(response);
      } else {
        actions.setError("Unexpected response format for document templates.");
      }
    } catch (error: any) {
      actions.setError("Error fetching document templates: " + error.message);
    } finally {
      actions.setLoading(false);
    }
  }),

  getJasperSubTemplateList: thunk(async (actions, { source }, { getState }) => {
    actions.setLoading(true);
    actions.setError(null);

    try {
      const responseData = await get(`getJasperSubTemplateList/${source}`);

      if (responseData && Array.isArray(responseData)) {
        const subContracts = responseData.map((resData) => ({
          label: resData.displayName,
          value: {
            id: resData.id,
            name: resData.templateName,
            documentSource: resData.documentSource,
            isJasper: resData.isJasper,
          },
        }));

        const documentTemplateOptions = getState().documentTemplates;

        actions.setAdditionalTemplateOptions([...subContracts, ...documentTemplateOptions]);
      } else {
        actions.setError("Unexpected response format for Jasper sub-templates.");
      }
    } catch (error: any) {
      actions.setError("Error fetching Jasper sub-template list: " + error.message);
    } finally {
      actions.setLoading(false);
    }
  }),

  generateDocumentFromTemplate: thunk(async (actions, params, { callBack }) => {
    actions.setLoading(true);
    actions.setError(null);

    try {
      const config = { timeout: 0 };
      const response = await post("generateDocument", params, config);
      if (callBack) {
        callBack(response);
      }
    } catch (error: any) {
      actions.setError("Error generating document: " + error.message);
    } finally {
      actions.setLoading(false);
    }
  }),

  generateDocumentWithMultipleTemplates: thunk(async (actions, params, { callBack }) => {
    actions.setLoading(true);
    actions.setError(null);

    try {
      const config = { timeout: 0 };
      const response = await post("generateDocumentForMultipleTemplates", params, config);
      if (callBack) {
        callBack(response);
      }
    } catch (error: any) {
      actions.setError("Error generating document with multiple templates: " + error.message);
    } finally {
      actions.setLoading(false);
    }
  }),

  getFunderBankDetails: thunk(async (actions, { dealId }) => {
    actions.setLoading(true);
    actions.setError(null);

    try {
      const responseData = await get(`deal/getFunder/${dealId}`);

      if (responseData) {
        const { routingNumber, accountNumber, bank, funder } = responseData;

        actions.setDocumentTemplates([{ routingNumber, accountNumber, bank, funder }]);
      } else {
        actions.setError("Unexpected response format for funder bank details.");
      }
    } catch (error: any) {
      actions.setError("Error fetching funder bank details: " + error.message);
    } finally {
      actions.setLoading(false);
    }
  }),

  fetchDocumentTemplatesByFunder: thunk(async (actions, request) => {
    let documentTemplateOptions = [];

    const funder = request?.funderName;
    const documentSource = request?.documentSource;

    const requestPath = funder
      ? `fetchDocumentTemplatesByFunder/${documentSource}/${funder}`
      : `fetchDocumentTemplates/${documentSource}`;

    try {
      const res = await get(requestPath);

      for (let index = 0; index < res.length; index++) {
        const resData = res[index];
        let documentInputsMap: any = {};

        resData.documentInputs.forEach((key: string) => {
          if (key === "bankName") documentInputsMap[key] = getState()?.bank;
          else if (key === "routingNumber") documentInputsMap[key] = getState()?.routingNumber;
          else if (key === "accountNumber") documentInputsMap[key] = getState()?.accountNumber;
          else documentInputsMap[key] = "";
        });

        let data = {
          label: resData.templateName,
          value: {
            name: resData.templateName,
            id: resData.id,
            documentSource: resData.documentSource,
            documentFunder: resData.documentFunder,
            documentInputs: documentInputsMap,
            templateType: resData.templateType,
            attachments: resData.attachments,
          },
        };
        documentTemplateOptions.push(data);
      }
      return documentTemplateOptions;
    } catch (error: any) {
      actions.setError("Error fetching document templates by funder: " + error.message);
      throw error;
    }
  }),
};

export default documentTemplateModel;
